import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, Layers, Activity, Eye as EyeIcon, CheckCircle2, Package, List, Kanban, GanttChart, ChevronRight, ChevronDown, ExternalLink, FolderOpen } from 'lucide-react';
import { FilterBar } from '@/components/ui/filter-bar';
import {
  useDeliverablesDashboard,
  useUpdateDeliverable,
  useDeleteDeliverable,
  type DeliverableDashboardFilters,
} from '@/hooks/useDeliverables';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { DeliverableTimeline } from './DeliverableTimeline';
import { DeliverableBoard } from './DeliverableBoard';
import { DeliverableRow } from './DeliverableRow';
import { isStale, isOverdue } from './deliverable-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { Deliverable, DeliverableStatus } from '@/hooks/useDeliverables';
import { useNavigate } from 'react-router-dom';

type ViewMode = 'list' | 'board' | 'timeline';

type FilterTab = 'all' | 'needs_attention' | 'in_progress' | 'in_review' | 'approved';

interface DeliverablesDashboardProps {
  initialFilter?: FilterTab;
  initialViewMode?: ViewMode;
  filters?: DeliverableDashboardFilters;
}

export function DeliverablesDashboard({ initialFilter = 'all', initialViewMode = 'list', filters }: DeliverablesDashboardProps = {}) {
  const [search, setSearch] = useState('');
  const mergedFilters = useMemo(
    () => ({ ...filters, ...(search ? { search } : {}) }),
    [filters, search]
  );
  const { data, isLoading } = useDeliverablesDashboard(mergedFilters);
  const { data: teamMembers } = useTeamMembers();
  const updateDeliverable = useUpdateDeliverable();
  const deleteDeliverable = useDeleteDeliverable();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterTab>(initialFilter);
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [expandedSubmissions, setExpandedSubmissions] = useState<Set<string>>(new Set());
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const hasAutoExpanded = useRef(false);

  // Derive all computed values safely (empty arrays when data not loaded)
  const deliverables = data?.deliverables ?? [];
  const stale_count = data?.stale_count ?? 0;
  const activeDeliverables = useMemo(
    () => deliverables.filter(d => d.status !== 'delivered'),
    [deliverables]
  );

  // Compute stats
  const stats = useMemo(
    () => activeDeliverables.reduce(
      (acc, d) => {
        acc[d.status] = (acc[d.status] || 0) + 1;
        if (isStale(d)) acc.stale += 1;
        if (isOverdue(d)) acc.overdue += 1;
        return acc;
      },
      { pending: 0, in_progress: 0, in_review: 0, revision: 0, approved: 0, delivered: 0, stale: 0, overdue: 0 } as Record<string, number>
    ),
    [activeDeliverables]
  );

  // Filter deliverables based on active tab
  const filteredDeliverables = useMemo(
    () => activeDeliverables.filter(d => {
      switch (activeFilter) {
        case 'needs_attention':
          return isStale(d) || isOverdue(d);
        case 'in_progress':
          return d.status === 'in_progress';
        case 'in_review':
          return d.status === 'in_review' || d.status === 'revision';
        case 'approved':
          return d.status === 'approved';
        default:
          return true;
      }
    }),
    [activeDeliverables, activeFilter]
  );

  // Group filtered deliverables by project for list view
  const projectGroups = useMemo(() => {
    const groups = new Map<string, {
      projectId: string;
      projectName: string;
      deliverables: Deliverable[];
      staleCount: number;
      attentionCount: number;
      submissions: Map<string, { name: string; deliverables: Deliverable[] }>;
      ungrouped: Deliverable[];
    }>();

    for (const d of filteredDeliverables) {
      const pid = d.project_id;
      if (!groups.has(pid)) {
        groups.set(pid, {
          projectId: pid,
          projectName: (d as any).project_name || 'Unknown Project',
          deliverables: [],
          staleCount: 0,
          attentionCount: 0,
          submissions: new Map(),
          ungrouped: [],
        });
      }
      const group = groups.get(pid)!;
      group.deliverables.push(d);
      if (isStale(d)) group.staleCount++;
      if (isStale(d) || isOverdue(d)) group.attentionCount++;

      if (d.submission_id && d.submission_name) {
        if (!group.submissions.has(d.submission_id)) {
          group.submissions.set(d.submission_id, { name: d.submission_name, deliverables: [] });
        }
        group.submissions.get(d.submission_id)!.deliverables.push(d);
      } else {
        group.ungrouped.push(d);
      }
    }

    // Sort: projects with attention items first, then by name
    return Array.from(groups.values()).sort((a, b) => {
      if (a.attentionCount > 0 && b.attentionCount === 0) return -1;
      if (a.attentionCount === 0 && b.attentionCount > 0) return 1;
      return a.projectName.localeCompare(b.projectName);
    });
  }, [filteredDeliverables]);

  // Auto-expand projects with attention items on first data load
  useEffect(() => {
    if (hasAutoExpanded.current || projectGroups.length === 0) return;
    hasAutoExpanded.current = true;
    const initial = new Set<string>();
    for (const g of projectGroups) {
      if (g.attentionCount > 0) initial.add(g.projectId);
    }
    // If no attention items, expand the first project
    if (initial.size === 0 && projectGroups.length > 0) initial.add(projectGroups[0].projectId);
    if (initial.size > 0) setExpandedProjects(initial);
  }, [projectGroups]);

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const toggleSubmission = useCallback((submissionId: string) => {
    setExpandedSubmissions(prev => {
      const next = new Set(prev);
      if (next.has(submissionId)) next.delete(submissionId);
      else next.add(submissionId);
      return next;
    });
  }, []);

  // Count unique deliverables needing attention (a deliverable can be both stale AND overdue)
  const attentionItems = useMemo(
    () => activeDeliverables.filter(d => isStale(d) || isOverdue(d)),
    [activeDeliverables]
  );
  const needsAttentionCount = attentionItems.length;
  const staleOnlyCount = attentionItems.filter(d => isStale(d)).length;
  const overdueOnlyCount = attentionItems.filter(d => isOverdue(d)).length;

  // Early returns AFTER all hooks
  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading deliverables...</div>;
  }

  if (!data) return null;

  // Empty state
  if (activeDeliverables.length === 0) {
    return (
      <div className="text-center py-16">
        <Layers className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground">No active deliverables</h3>
        <p className="text-sm text-muted-foreground/70 mt-2 max-w-md mx-auto">
          Deliverables are tracked per project. Open a project and add deliverables to see them here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      {/* Search */}
      <FilterBar
        search={search}
        onSearchChange={setSearch}
        placeholder="Search deliverables..."
        onClearAll={() => setSearch('')}
        className="rounded-lg border bg-card p-3"
      />

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard
          label="Active"
          value={activeDeliverables.length}
          icon={<Layers className="h-4 w-4" />}
          color="text-foreground"
        />
        <StatCard
          label="In Progress"
          value={stats.in_progress}
          icon={<Activity className="h-4 w-4" />}
          color="text-blue-600"
        />
        <StatCard
          label="In Review"
          value={stats.in_review + stats.revision}
          icon={<EyeIcon className="h-4 w-4" />}
          color="text-yellow-600"
          sublabel={[
            stats.in_review > 0 ? `${stats.in_review} review` : '',
            stats.revision > 0 ? `${stats.revision} revision` : '',
          ].filter(Boolean).join(', ') || undefined}
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          icon={<CheckCircle2 className="h-4 w-4" />}
          color="text-green-600"
        />
        {needsAttentionCount > 0 ? (
          <StatCard
            label="Needs Attention"
            value={needsAttentionCount}
            icon={<AlertTriangle className="h-4 w-4" />}
            color="text-red-600"
            sublabel={[
              staleOnlyCount > 0 ? `${staleOnlyCount} stale` : '',
              overdueOnlyCount > 0 ? `${overdueOnlyCount} overdue` : '',
            ].filter(Boolean).join(', ')}
            highlight
          />
        ) : (
          <StatCard
            label="Pending"
            value={stats.pending}
            icon={<Package className="h-4 w-4" />}
            color="text-muted-foreground"
          />
        )}
      </div>

      {/* View switcher + filter tabs */}
      <div className="flex items-center gap-1 border-b overflow-x-auto">
        {/* Filter tabs (only visible in list view) */}
        {viewMode === 'list' && (
          <>
            <FilterTabButton
              label="All"
              count={activeDeliverables.length}
              active={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            />
            {needsAttentionCount > 0 && (
              <FilterTabButton
                label="Needs Attention"
                count={needsAttentionCount}
                active={activeFilter === 'needs_attention'}
                onClick={() => setActiveFilter('needs_attention')}
                alert
              />
            )}
            <FilterTabButton
              label="In Progress"
              count={stats.in_progress}
              active={activeFilter === 'in_progress'}
              onClick={() => setActiveFilter('in_progress')}
            />
            <FilterTabButton
              label="Review"
              count={stats.in_review + stats.revision}
              active={activeFilter === 'in_review'}
              onClick={() => setActiveFilter('in_review')}
            />
            <FilterTabButton
              label="Approved"
              count={stats.approved}
              active={activeFilter === 'approved'}
              onClick={() => setActiveFilter('approved')}
            />
          </>
        )}

        {/* Spacer when in list mode, otherwise left-align view buttons */}
        <div className="flex-1" />

        {/* View mode toggle */}
        <div className="flex items-center gap-0.5 mr-1 mb-[-2px]">
          {([
            { mode: 'list' as ViewMode, icon: List, label: 'List' },
            { mode: 'board' as ViewMode, icon: Kanban, label: 'Board' },
            { mode: 'timeline' as ViewMode, icon: GanttChart, label: 'Timeline' },
          ]).map(({ mode, icon: Icon, label }) => (
            <button
              key={mode}
              className={`flex items-center gap-1 px-2.5 py-2 text-xs font-medium border-b-2 transition-colors ${
                viewMode === mode
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setViewMode(mode)}
              title={label}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Stale warning banner - shown in list and board views */}
      {stale_count > 0 && viewMode !== 'timeline' && (activeFilter === 'all' || activeFilter === 'needs_attention' || viewMode !== 'list') && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              {stale_count} deliverable{stale_count !== 1 ? 's' : ''} stale
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              In review or revision for 3+ days.
            </p>
          </div>
        </div>
      )}

      {/* View content */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {filteredDeliverables.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No deliverables match this filter.
            </div>
          ) : (
            projectGroups.map((group) => {
              const isProjectExpanded = expandedProjects.has(group.projectId);

              return (
                <div key={group.projectId} className="rounded-lg border overflow-hidden">
                  {/* Project header */}
                  <div
                    className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors hover:bg-accent/50 ${
                      group.attentionCount > 0 ? 'bg-red-50/30' : 'bg-muted/30'
                    }`}
                    onClick={() => toggleProject(group.projectId)}
                  >
                    {isProjectExpanded
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    }
                    <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-semibold truncate">{group.projectName}</span>
                    <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">
                      {group.deliverables.length} {group.deliverables.length === 1 ? 'item' : 'items'}
                    </span>
                    {group.staleCount > 0 && (
                      <span className="flex items-center gap-1 text-xs text-red-600 font-medium whitespace-nowrap">
                        <AlertTriangle className="h-3 w-3" />
                        {group.staleCount} stale
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 opacity-60 hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); navigate(`/projects/${group.projectId}?section=deliverables`); }}
                      title="Open project"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Expanded project content */}
                  {isProjectExpanded && (
                    <div className="border-t">
                      {/* Submission sub-groups (collapsed by default) */}
                      {group.submissions.size > 0 && (
                        <div className="divide-y">
                          {Array.from(group.submissions.entries()).map(([subId, sub]) => {
                            const delivered = sub.deliverables.filter(d => d.status === 'delivered' || d.status === 'approved').length;
                            const active = sub.deliverables.filter(d => ['in_progress', 'in_review', 'revision'].includes(d.status)).length;
                            const total = sub.deliverables.length;
                            const pct = total > 0 ? Math.round((delivered / total) * 100) : 0;
                            const isSubExpanded = expandedSubmissions.has(subId);

                            return (
                              <div key={subId} className="px-3 py-2 bg-muted/10">
                                <div
                                  className="flex items-center gap-2 ml-5 cursor-pointer hover:bg-accent/30 rounded -mx-1 px-1 py-0.5 transition-colors"
                                  onClick={() => toggleSubmission(subId)}
                                >
                                  {isSubExpanded
                                    ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                                    : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  }
                                  <span className="text-xs font-medium">{sub.name}</span>
                                  <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden flex">
                                    {delivered > 0 && (
                                      <div className="h-full bg-green-500" style={{ width: `${(delivered / total) * 100}%` }} />
                                    )}
                                    {active > 0 && (
                                      <div className="h-full bg-blue-400" style={{ width: `${(active / total) * 100}%` }} />
                                    )}
                                  </div>
                                  <span className="text-[10px] text-muted-foreground">
                                    {delivered}/{total}{active > 0 && pct < 100 && (
                                      <span className="text-blue-600"> ({active} active)</span>
                                    )}
                                  </span>
                                </div>
                                {/* Deliverable rows in this submission */}
                                {isSubExpanded && (
                                  <div className="mt-1 space-y-0.5">
                                    {sub.deliverables.map(d => (
                                      <DeliverableRow
                                        key={d.id}
                                        deliverable={d}
                                        expandedId={expandedId}
                                        onToggle={() => setExpandedId(expandedId === d.id ? null : d.id)}
                                        onStatusChange={(id, status) => updateDeliverable.mutateAsync({ id, data: { status } })}
                                        onDelete={setDeleteId}
                                        onNavigate={navigate}
                                        teamMembers={teamMembers}
                                        onUpdate={(id, data) => updateDeliverable.mutateAsync({ id, data })}
                                        compact
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Ungrouped deliverables (no submission) */}
                      {group.ungrouped.length > 0 && (
                        <div className={group.submissions.size > 0 ? 'border-t' : ''}>
                          <div className="space-y-0.5 py-1">
                            {group.ungrouped.map(d => (
                              <DeliverableRow
                                key={d.id}
                                deliverable={d}
                                expandedId={expandedId}
                                onToggle={() => setExpandedId(expandedId === d.id ? null : d.id)}
                                onStatusChange={(id, status) => updateDeliverable.mutateAsync({ id, data: { status } })}
                                onDelete={setDeleteId}
                                onNavigate={navigate}
                                teamMembers={teamMembers}
                                onUpdate={(id, data) => updateDeliverable.mutateAsync({ id, data })}
                                compact={false}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {viewMode === 'board' && (
        <DeliverableBoard deliverables={activeDeliverables} />
      )}

      {viewMode === 'timeline' && (
        <DeliverableTimeline deliverables={deliverables} />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete deliverable?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const target = activeDeliverables.find(d => d.id === deleteId);
                return target
                  ? <>This will permanently remove <span className="font-medium text-foreground">"{target.title}"</span>. This action cannot be undone.</>
                  : 'This will permanently remove this deliverable. This action cannot be undone.';
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteId) return;
                await deleteDeliverable.mutateAsync(deleteId);
                setDeleteId(null);
                if (expandedId === deleteId) setExpandedId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// --- Sub-components ---

function StatCard({
  label,
  value,
  icon,
  color,
  sublabel,
  highlight,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  sublabel?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        highlight ? 'border-red-200 bg-red-50/50' : 'bg-card'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={`${color} opacity-70`}>{icon}</span>
        <span className={`text-2xl font-bold ${color}`}>{value}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sublabel && (
        <p className={`text-[10px] mt-0.5 ${highlight ? 'text-red-500' : 'text-muted-foreground/70'}`}>
          {sublabel}
        </p>
      )}
    </div>
  );
}

function FilterTabButton({
  label,
  count,
  active,
  onClick,
  alert,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  alert?: boolean;
}) {
  return (
    <button
      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? alert
            ? 'border-red-600 text-red-600'
            : 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
      }`}
      onClick={onClick}
    >
      {label}
      {count > 0 && (
        <span
          className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
            active
              ? alert
                ? 'bg-red-100 text-red-700'
                : 'bg-primary/10 text-primary'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
